import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/auth';
import { useProjectsStore } from '../store/projects';
import { useMarketingStore, ContentPillar, CreatePillarInput, UpdatePillarInput } from '../store/marketing';
import { PageHeader } from '../components/PageHeader';

// PillarIcon - matches header navigation (paint/brush icon)
function PillarIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
    </svg>
  );
}

// Preset colors for pillars
const PRESET_COLORS = [
  '#3B82F6', // Blue
  '#8B5CF6', // Purple
  '#EF4444', // Red
  '#22C55E', // Green
  '#F97316', // Orange
  '#EC4899', // Pink
  '#06B6D4', // Cyan
  '#84CC16', // Lime
  '#F59E0B', // Amber
  '#6366F1', // Indigo
];

// Format icons (labels are translated)
const FORMAT_ICONS: Record<string, string> = {
  carousel: '📸',
  reel: '🎬',
  story: '📱',
  single_graphic: '🖼️',
  text: '📝',
};

// Pillar Modal Component
function PillarModal({
  pillar,
  onClose,
  onSave,
  isLoading,
  t,
}: {
  pillar?: ContentPillar | null;
  onClose: () => void;
  onSave: (data: CreatePillarInput | UpdatePillarInput) => Promise<void>;
  isLoading: boolean;
  t: (key: string) => string;
}) {
  const [name, setName] = useState(pillar?.name || '');
  const [description, setDescription] = useState(pillar?.description || '');
  const [color, setColor] = useState(pillar?.color || PRESET_COLORS[0]);
  const [icon, setIcon] = useState(pillar?.icon || '');
  const [recommendedFormats, setRecommendedFormats] = useState<string[]>(
    pillar?.recommended_formats || []
  );
  const [guideAnchors, setGuideAnchors] = useState<string>(
    pillar?.guide_anchors?.join('\n') || ''
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSave({
      name,
      description: description || undefined,
      color,
      icon: icon || undefined,
      recommended_formats: recommendedFormats,
      guide_anchors: guideAnchors.split('\n').filter(Boolean),
    });
  };

  const toggleFormat = (format: string) => {
    setRecommendedFormats((prev) =>
      prev.includes(format) ? prev.filter((f) => f !== format) : [...prev, format]
    );
  };

  // Build format options with translated labels
  const formatOptions = Object.entries(FORMAT_ICONS).map(([value, icon]) => ({
    value,
    label: t(`marketing:formats.${value}`),
    icon,
  }));

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className="fixed inset-0 bg-black/50 transition-opacity"
          onClick={onClose}
        />

        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg transform transition-all">
          <div className="flex items-center justify-between p-4 border-b">
            <h3 className="text-lg font-semibold text-gray-900">
              {pillar ? t('marketing:pillars.editPillar') : t('marketing:pillars.newPillar')}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-4 space-y-4">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('marketing:pillars.nameRequired')}
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('marketing:pillars.namePlaceholder')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('marketing:pillars.description')}
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t('marketing:pillars.descriptionPlaceholder')}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Color */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('marketing:pillars.color')}
              </label>
              <div className="flex flex-wrap gap-2">
                {PRESET_COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setColor(c)}
                    className={`w-8 h-8 rounded-full transition-all ${
                      color === c ? 'ring-2 ring-offset-2 ring-gray-400 scale-110' : ''
                    }`}
                    style={{ backgroundColor: c }}
                  />
                ))}
                <input
                  type="color"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  className="w-8 h-8 rounded cursor-pointer"
                  title={t('marketing:pillars.customColor')}
                />
              </div>
            </div>

            {/* Icon */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('marketing:pillars.icon')}
              </label>
              <input
                type="text"
                value={icon}
                onChange={(e) => setIcon(e.target.value)}
                placeholder={t('marketing:pillars.iconPlaceholder')}
                maxLength={4}
                className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center text-xl"
              />
            </div>

            {/* Recommended Formats */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('marketing:pillars.recommendedFormats')}
              </label>
              <div className="flex flex-wrap gap-2">
                {formatOptions.map((format) => (
                  <button
                    key={format.value}
                    type="button"
                    onClick={() => toggleFormat(format.value)}
                    className={`px-3 py-1.5 rounded-lg text-sm transition-all ${
                      recommendedFormats.includes(format.value)
                        ? 'bg-blue-100 text-blue-700 border border-blue-300'
                        : 'bg-gray-100 text-gray-600 border border-gray-200 hover:bg-gray-200'
                    }`}
                  >
                    {format.icon} {format.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Guide Anchors */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('marketing:pillars.guideAnchors')}
              </label>
              <textarea
                value={guideAnchors}
                onChange={(e) => setGuideAnchors(e.target.value)}
                placeholder={t('marketing:pillars.guideAnchorsPlaceholder')}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm font-mono"
              />
              <p className="text-xs text-gray-500 mt-1">
                {t('marketing:pillars.guideAnchorsHint')}
              </p>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                {t('common:cancel')}
              </button>
              <button
                type="submit"
                disabled={isLoading || !name.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {isLoading ? t('common:saving') : pillar ? t('common:save') : t('common:create')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// Delete Confirmation Modal
function DeleteModal({
  pillar,
  onClose,
  onConfirm,
  isLoading,
  t,
}: {
  pillar: ContentPillar;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  isLoading: boolean;
  t: (key: string) => string;
}) {
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className="fixed inset-0 bg-black/50 transition-opacity"
          onClick={onClose}
        />

        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md transform transition-all p-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{t('marketing:pillars.deletePillarConfirm')}</h3>
              <p className="text-sm text-gray-500">
                "{pillar.name}" {t('marketing:pillars.willBeDeleted')}
              </p>
            </div>
          </div>

          <p className="text-sm text-gray-600 mb-6">
            {t('marketing:pillars.deletePillarWarning')}
          </p>

          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              {t('common:cancel')}
            </button>
            <button
              onClick={onConfirm}
              disabled={isLoading}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
            >
              {isLoading ? t('common:deleting') : t('common:delete')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function MarketingPillarsPage() {
  const { t } = useTranslation(['marketing', 'common']);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const { projects, fetchProjects, selectedProjectId, setSelectedProjectId } = useProjectsStore();
  const {
    pillars,
    editorialWeeks,
    isLoading,
    error,
    fetchPillars,
    fetchEditorialPlan,
    createPillar,
    updatePillar,
    deletePillar,
    clearError,
  } = useMarketingStore();

  const [showModal, setShowModal] = useState(false);
  const [editingPillar, setEditingPillar] = useState<ContentPillar | null>(null);
  const [deletingPillar, setDeletingPillar] = useState<ContentPillar | null>(null);
  const [showInactive, setShowInactive] = useState(false);

  // Build format options with translated labels
  const formatOptions = Object.entries(FORMAT_ICONS).map(([value, icon]) => ({
    value,
    label: t(`marketing:formats.${value}`),
    icon,
  }));

  // Auth check
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchProjects();
  }, [isAuthenticated, navigate, fetchProjects]);

  // Set project from URL or default
  useEffect(() => {
    const projectIdParam = searchParams.get('project_id');
    if (projectIdParam && projects.some((p) => p.id === projectIdParam)) {
      setSelectedProjectId(projectIdParam);
    } else if (projects.length > 0 && !selectedProjectId) {
      setSelectedProjectId(projects[0].id);
    }
  }, [searchParams, projects, selectedProjectId, setSelectedProjectId]);

  // Load data
  useEffect(() => {
    if (selectedProjectId) {
      fetchPillars(selectedProjectId, showInactive);
      fetchEditorialPlan(selectedProjectId, new Date().getFullYear());
    }
  }, [selectedProjectId, showInactive, fetchPillars, fetchEditorialPlan]);

  const handleCreatePillar = async (data: CreatePillarInput | UpdatePillarInput) => {
    if (!selectedProjectId) return;
    await createPillar({
      ...data,
      project_id: selectedProjectId,
    } as CreatePillarInput);
    setShowModal(false);
  };

  const handleUpdatePillar = async (data: UpdatePillarInput) => {
    if (!editingPillar) return;
    await updatePillar(editingPillar.id, data);
    setEditingPillar(null);
  };

  const handleDeletePillar = async () => {
    if (!deletingPillar) return;
    await deletePillar(deletingPillar.id);
    setDeletingPillar(null);
  };

  const handleToggleActive = async (pillar: ContentPillar) => {
    await updatePillar(pillar.id, { is_active: !pillar.is_active });
  };

  // Count weeks per pillar
  const getWeekCount = (pillarId: string) => {
    return editorialWeeks.filter((w) => w.pillar_id === pillarId).length;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <PageHeader
        icon={<PillarIcon className="w-7 h-7" />}
        title={t('marketing:pillars.title')}
        subtitle={t('marketing:pillars.subtitle')}
        breadcrumbs={[{ label: t('marketing:breadcrumbs.marketing'), href: '/marketing' }]}
        actions={
          <div className="flex items-center gap-3">
            {/* Project selector */}
            <select
              value={selectedProjectId || ''}
              onChange={(e) => {
                setSelectedProjectId(e.target.value);
                navigate(`/marketing/pillars?project_id=${e.target.value}`);
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
            >
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>

            <button
              onClick={() => setShowModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              {t('marketing:pillars.newPillar')}
            </button>
          </div>
        }
      />

      {/* Error display */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <svg className="w-5 h-5 text-red-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="flex-1">
            <p className="text-red-700">{error}</p>
          </div>
          <button
            onClick={clearError}
            className="text-red-500 hover:text-red-700"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Filter */}
      <div className="mb-6">
        <label className="inline-flex items-center gap-2 text-sm text-gray-600">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          {t('marketing:pillars.showInactive')}
        </label>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-gray-500">{t('common:loading')}</div>
        </div>
      ) : pillars.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {t('marketing:pillars.empty.title')}
          </h3>
          <p className="text-gray-500 mb-4">
            {t('marketing:pillars.empty.description')}
          </p>
          <button
            onClick={() => setShowModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {t('marketing:pillars.createFirst')}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {pillars.map((pillar) => {
            const weekCount = getWeekCount(pillar.id);
            return (
              <div
                key={pillar.id}
                className={`bg-white rounded-xl shadow-sm border overflow-hidden transition-all hover:shadow-md ${
                  pillar.is_active ? 'border-gray-200' : 'border-gray-100 opacity-60'
                }`}
              >
                {/* Color Header */}
                <div
                  className="h-2"
                  style={{ backgroundColor: pillar.color }}
                />

                <div className="p-5">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      {pillar.icon && (
                        <span className="text-2xl">{pillar.icon}</span>
                      )}
                      <div>
                        <h3 className="font-semibold text-gray-900">{pillar.name}</h3>
                        <span className="text-xs text-gray-400">{pillar.display_id}</span>
                      </div>
                    </div>

                    {/* Actions Menu */}
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setEditingPillar(pillar)}
                        className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                        title={t('common:edit')}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleToggleActive(pillar)}
                        className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                        title={pillar.is_active ? t('marketing:pillars.deactivate') : t('marketing:pillars.activate')}
                      >
                        {pillar.is_active ? (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                          </svg>
                        ) : (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        )}
                      </button>
                      <button
                        onClick={() => setDeletingPillar(pillar)}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                        title={t('common:delete')}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* Description */}
                  {pillar.description && (
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                      {pillar.description}
                    </p>
                  )}

                  {/* Stats */}
                  <div className="flex items-center gap-4 mb-3 text-sm">
                    <div className="flex items-center gap-1.5 text-gray-500">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      {weekCount} {t('marketing:pillars.weeks')}
                    </div>
                    {!pillar.is_active && (
                      <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-500 rounded">
                        {t('marketing:pillars.inactive')}
                      </span>
                    )}
                  </div>

                  {/* Recommended Formats */}
                  {pillar.recommended_formats && pillar.recommended_formats.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {pillar.recommended_formats.map((format) => {
                        const formatInfo = formatOptions.find((f) => f.value === format);
                        return (
                          <span
                            key={format}
                            className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded"
                          >
                            {formatInfo?.icon} {formatInfo?.label || format}
                          </span>
                        );
                      })}
                    </div>
                  )}

                  {/* Guide Anchors */}
                  {pillar.guide_anchors && pillar.guide_anchors.length > 0 && (
                    <div className="pt-3 border-t border-gray-100">
                      <span className="text-xs text-gray-400">{t('marketing:pillars.chapterReferences')}</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {pillar.guide_anchors.slice(0, 3).map((anchor, i) => (
                          <span
                            key={i}
                            className="px-1.5 py-0.5 text-xs bg-blue-50 text-blue-600 rounded"
                          >
                            {anchor}
                          </span>
                        ))}
                        {pillar.guide_anchors.length > 3 && (
                          <span className="px-1.5 py-0.5 text-xs text-gray-400">
                            +{pillar.guide_anchors.length - 3}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create Modal */}
      {showModal && (
        <PillarModal
          onClose={() => setShowModal(false)}
          onSave={handleCreatePillar}
          isLoading={isLoading}
          t={t}
        />
      )}

      {/* Edit Modal */}
      {editingPillar && (
        <PillarModal
          pillar={editingPillar}
          onClose={() => setEditingPillar(null)}
          onSave={handleUpdatePillar}
          isLoading={isLoading}
          t={t}
        />
      )}

      {/* Delete Modal */}
      {deletingPillar && (
        <DeleteModal
          pillar={deletingPillar}
          onClose={() => setDeletingPillar(null)}
          onConfirm={handleDeletePillar}
          isLoading={isLoading}
          t={t}
        />
      )}
    </div>
  );
}
